import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Lightbulb, RefreshCw, Sparkles, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { HEAT, useAiStatus, type Diagnosis, type Heat, type QuickNoteResult } from '../../lib/ai';
import { useLeadSetup, whatsappLink, type LeadDetail } from '../../lib/leads';
import { Badge, Button, CopyBox, Modal, SelectField, TextAreaField, cx } from '../ui';
import { FollowupPicker } from './bits';

/** 🔥 Hot / 🌤️ Warm / ❄️ Cold, with the AI's reason on hover. */
export function HeatBadge({ label, reason }: { label: Heat | null; reason?: string | null }) {
  if (!label) return null;
  const h = HEAT[label];
  return <span title={reason ?? undefined}><Badge tone={h.tone}>{h.emoji} {h.label}</Badge></span>;
}

/** Can this person use AI right now? (Hides AI buttons when AI isn't set up for the platform.) */
export function useCanUseAi() {
  const { can } = useAuth();
  const s = useAiStatus();
  return { show: can('ai.use') && !!s.data && s.data.reason !== 'not_set_up', ready: !!s.data?.ready, status: s.data };
}

/** The AI strip on a lead: how hot it is and why, plus advice on how to win it. */
export function LeadAiPanel({ lead, onChanged }: { lead: LeadDetail; onChanged: () => void }) {
  const ai = useCanUseAi();
  const [advice, setAdvice] = useState<Diagnosis | null>(null);
  const score = useMutation({
    mutationFn: () => api.post<{ score: number; label: Heat; reason: string }>(`/ai/leads/${lead.id}/score`),
    onSuccess: () => onChanged(),
    onError: (e) => toast.error(e.message),
  });
  const advise = useMutation({
    mutationFn: () => api.post<Diagnosis>(`/ai/leads/${lead.id}/advice`),
    onSuccess: setAdvice,
    onError: (e) => toast.error(e.message),
  });
  if (!ai.show) return null;
  const h = lead.aiLabel ? HEAT[lead.aiLabel] : null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-100 bg-gradient-to-r from-brand-50/80 to-white p-3 dark:border-slate-700 dark:from-slate-800/60 dark:to-slate-900">
      <Sparkles className="size-5 shrink-0 text-brand-600" />
      <div className="min-w-0 flex-1 text-sm">
        {h ? (
          <p><strong>{h.emoji} {h.label}{lead.score != null ? ` · ${lead.score}/100` : ''}</strong>{lead.aiReason && <span className="text-slate-600 dark:text-slate-400"> — {lead.aiReason}</span>}</p>
        ) : (
          <p className="text-slate-600 dark:text-slate-400">AI hasn’t rated this lead yet.</p>
        )}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" icon={<RefreshCw className="size-4" />} disabled={!ai.ready} loading={score.isPending} onClick={() => score.mutate()}>
          {h ? 'Re-rate' : 'Rate now'}
        </Button>
        <Button size="sm" variant="secondary" icon={<Lightbulb className="size-4" />} disabled={!ai.ready} loading={advise.isPending} onClick={() => advise.mutate()}>AI advice</Button>
      </div>
      {advice && <AdviceModal advice={advice} lead={lead} onClose={() => setAdvice(null)} />}
    </div>
  );
}

function AdviceModal({ advice, lead, onClose }: { advice: Diagnosis; lead: LeadDetail; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={`How to win ${lead.name}`} size="lg"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      <div className="space-y-5 text-sm">
        <div className="flex items-start gap-2">
          <HeatBadge label={advice.temperature} />
          <p className="text-slate-700 dark:text-slate-300">{advice.summary}</p>
        </div>
        <div>
          <p className="mb-1.5 font-semibold">What to do next</p>
          <ol className="list-decimal space-y-1 pl-5">{advice.nextSteps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </div>
        {advice.risks.length > 0 && (
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 font-semibold"><TriangleAlert className="size-4 text-amber-500" /> Watch out for</p>
            <ul className="list-disc space-y-1 pl-5">{advice.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        )}
        <div className="space-y-2">
          <CopyBox label="Message you can send" value={advice.suggestedMessage} />
          {lead.phone && (
            <a href={whatsappLink(lead.phone, advice.suggestedMessage)} target="_blank" rel="noreferrer" className="inline-block text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400">
              Open in WhatsApp →
            </a>
          )}
        </div>
        <p className="text-xs text-slate-500">AI advice is a suggestion — use your own judgement.</p>
      </div>
    </Modal>
  );
}

/** Review screen for an AI-tidied note: nothing is saved until the person confirms. */
export function QuickNoteModal({ lead, result, onClose, onSaved }: { lead: LeadDetail; result: QuickNoteResult; onClose: () => void; onSaved: () => void }) {
  const setup = useLeadSetup().data;
  const [note, setNote] = useState(result.note);
  const [at, setAt] = useState(result.followupAt ?? '');
  const [fnote, setFnote] = useState(result.followupNote ?? '');
  const [statusId, setStatusId] = useState(result.statusId ?? lead.statusId);
  const save = useMutation({
    mutationFn: async () => {
      if (note.trim()) await api.post(`/leads/${lead.id}/notes`, { body: note.trim() });
      if (statusId !== lead.statusId) await api.patch(`/leads/${lead.id}`, { statusId });
      if (at) await api.post(`/leads/${lead.id}/followup`, { at, note: fnote });
    },
    onSuccess: () => { toast.success('Saved.'); onSaved(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Modal open onClose={onClose} title="Check and save" size="md"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={save.isPending} onClick={() => save.mutate()}>Save all</Button></>}>
      <div className="space-y-5">
        <TextAreaField label="Note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
        <SelectField label="Status" value={statusId} onChange={(e) => setStatusId(e.target.value)}
          hint={result.statusName && result.statusId !== lead.statusId ? `AI suggests “${result.statusName}”.` : undefined}>
          {setup?.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SelectField>
        <div>
          <p className={cx('mb-2 text-sm font-medium')}>{result.followupAt ? 'Follow-up AI found in your note' : 'Follow-up (optional)'}</p>
          <FollowupPicker value={at} onChange={setAt} note={fnote} onNoteChange={setFnote} />
        </div>
      </div>
    </Modal>
  );
}
